import HighchartsGantt from "highcharts/highcharts-gantt";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import dayjs from "dayjs";
import { TaskView } from "src/api/generated/postgres/db/types/tasks/tables/taskView.ts";
import { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { getTaskStateLabel } from "src/api/generated/io/aavo/applications/db/postgres/enums/taskState.ts";
import {
	dayJsToDateIsoString,
	dayJsToDateIsoStringNullable,
	formatDayJs,
	formatIsoString,
} from "src/utils/dayjsUtils.ts";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import {
	OpenTaskContextMenuFunc,
	useTaskContextMenu,
} from "src/components/views/tasks/desktop/TaskContextMenu.tsx";
import { OpenGenericDialogFunc } from "src/components/common/dialogs/GenericDialogProvider.tsx";
import { useGenericDialog } from "src/components/common/dialogs/useGenericDialog.ts";
import { TaskDesktopForm } from "src/components/views/tasks/desktop/TaskDesktopForm.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/useConfirmDialog.ts";
import { ShowConfirmDialogFunc } from "src/components/common/dialogs/confirmDialog/ConfirmDialogProvider.tsx";
import i18n from "i18next";
import { TaskUpdateApi } from "src/api/generated/tasks/api/taskUpdateApi.ts";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/userErrorDialog.ts";
import { ShowErrorDialogFunc } from "src/components/common/dialogs/errorDialog/ErrorDialogProvider.tsx";
import { AnyError } from "src/errorHandling/anyError.ts";
import { useMemo, useRef } from "react";
import { useResizeObserver } from "src/utils/useResizeObserver.ts";

export interface TasksGanttViewProps {
	tasks: TaskView[];
	reload: () => Promise<unknown>;
	renderTaskLabel?: (task: TaskView) => string;
	renderTaskCategory?: (task: TaskView) => string;
}

export const TasksGanttView = (props: TasksGanttViewProps) => {
	const { tasks, reload } = props;
	const theme = useTheme();
	const { openTaskContextMenu, taskContextMenuState } = useTaskContextMenu({
		refreshData: reload,
	});
	const { openDialog } = useGenericDialog();
	const containerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<HighchartsReactRefObject>(null);
	const confirm = useConfirmDialog();
	const showError = useErrorDialog();

	// Options must be memoized to prevent dragged tasks from being reset during the confirm.
	// Most deps are functions that are not memoized, but they should not change during the lifetime of the component.
	const options = useMemo(
		() => getChartOptions(props, theme, openTaskContextMenu, openDialog, confirm, showError),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[tasks, theme, reload],
	);

	const setChartHeight = () => {
		if (!chartRef.current || !containerRef.current) return;
		const containerHeight = containerRef.current.clientHeight;

		chartRef.current.chart.update(
			{
				chart: {
					height: containerHeight,
					scrollablePlotArea: {
						minHeight: containerHeight + 10,
					},
				},
			},
			true,
			false,
			false,
		);
	};

	useResizeObserver(
		containerRef,
		() => {
			setChartHeight();
		},
		[chartRef.current],
	);

	return (
		<VerticalBox
			ref={containerRef}
			className={"test"}
			sx={{
				flex: 1,
				display: "flex",
				alignItems: "stretch",
				overflow: "hidden",
				position: "relative",
				"& > *": {
					flex: 1,
				},
			}}
		>
			<HighchartsReact
				ref={chartRef}
				highcharts={HighchartsGantt}
				constructorType={"ganttChart"}
				allowChartUpdate={true}
				immutable={false}
				options={options}
			/>
			<AavoContextMenu state={taskContextMenuState} />
		</VerticalBox>
	);
};

const getChartOptions = (
	props: TasksGanttViewProps,
	theme: Theme,
	openContextMenu: OpenTaskContextMenuFunc,
	openDialog: OpenGenericDialogFunc,
	confirm: ShowConfirmDialogFunc,
	showError: ShowErrorDialogFunc,
): HighchartsGantt.Options => {
	const {
		tasks,
		reload,
		renderTaskLabel = (task) => task.title,
		renderTaskCategory = (task) => task.title,
	} = props;

	const getTaskById = (taskId: number) => tasks.find((task) => task.taskId === taskId);

	return {
		chart: {
			scrollablePlotArea: {
				opacity: 0.5,
			},
		},
		title: {
			text: undefined,
		},
		yAxis: {
			title: {
				text: null,
			},
			categories: tasks.map((task) => renderTaskCategory(task)),
			staticScale: Y_AXIS_STATIC_SCALE,
			showEmpty: false,
			visible: false,
		},
		navigator: {
			enabled: true,
		},
		rangeSelector: {
			enabled: true,
		},
		legend: {
			enabled: false,
		},
		series: [
			{
				type: "gantt",
				data: tasks.map((task, idx) => ({
					id: task.taskId.toString(),
					name: renderTaskLabel(task),
					y: idx,
					start: (task.earliestStartDate
						? dayjs(task.earliestStartDate)
						: dayjs(task.deadlineDate)
					)?.valueOf(),
					end: dayjs(task.deadlineDate).add(1, "day").valueOf(),
					color: getTaskColor(task, theme),
					tooltip: getTooltip(task),
					pointWidth: Y_AXIS_STATIC_SCALE - 10,
				})),
			},
		],
		plotOptions: {
			gantt: {
				dragDrop: {
					draggableX: true,
					draggableY: false,
					dragPrecisionX: dayjs.duration(1, "day").asMilliseconds(),
					liveRedraw: true,
				},
				dataLabels: {
					enabled: true,
					format: "{point.name}",
				},
				tooltip: {
					headerFormat: "",
					pointFormat: "{point.tooltip}",
				},
				point: {
					events: {
						contextmenu: (event) => {
							const taskId = Number(event.point.options.id);
							const task = getTaskById(taskId);
							if (task == null) return;
							openContextMenu(event, [task]);
						},
						dblclick: (event) => {
							const taskId = Number(event.point.options.id);
							const task = getTaskById(taskId);
							if (task == null) return;
							openDialog(({ closeDialog, onContentEdited }) => ({
								title: task.title,
								size: "lg",
								children: (
									<TaskDesktopForm
										taskId={task.taskId}
										newTaskSourceRef={undefined}
										onCompleted={async () => {
											await closeDialog();
											await reload();
										}}
										onFormEdited={onContentEdited}
									/>
								),
							}));
						},
						drop: async (point: any) => {
							const taskId = Number(point.target.id);
							const task = getTaskById(taskId);
							if (!task) return false;

							const newDeadlineDate = dayjs(point.target.end).subtract(1, "day");
							const newEarliestStartDate =
								task.earliestStartDate != null ? dayjs(point.target.start) : null;

							const confirmed = await confirm({
								message: i18n.t("confirm_reschedule_task", {
									newDeadlineDate: formatDayJs(newDeadlineDate),
									newEarliestStartDate: formatDayJs(newEarliestStartDate),
								}),
							});
							if (!confirmed) {
								await reload();
								return false;
							}

							try {
								await TaskUpdateApi.scheduleTasks({
									taskIds: [taskId],
									earliestStartDate:
										dayJsToDateIsoStringNullable(newEarliestStartDate),
									deadlineDate: dayJsToDateIsoString(newDeadlineDate),
								});
							} catch (e) {
								showError(e as AnyError);
							} finally {
								await reload();
							}
							return true;
						},
					},
				},
			},
		},
	};
};

const getTaskColor = (task: TaskView, theme: Theme) => {
	if (task.taskState === "COMPLETED") return theme.palette.success.main;
	if (task.taskIsLate) return theme.palette.error.light;
	return theme.palette.primary.main;
};

const getTooltip = (task: TaskView) => {
	return `
		<b>Tehtävä:</b> ${task.title}<br/>
		<b>Lähde:</b> ${task.sourceDescription}<br/>
		<b>Tila:</b> ${getTaskStateLabel(task.taskState)}<br/>
		<b>Tekijä:</b> ${task.assignedToUserName}<br/>
		<b>Valmiina viimeistään:</b> ${formatIsoString(task.deadlineDate)}<br/>
		<b>Aloitus aikaisintaan:</b> ${formatIsoString(task.earliestStartDate)}
	`;
};

const Y_AXIS_STATIC_SCALE = 35;
